import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import RowActionsMenu from "../../components/RowActionsMenu";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MetricsStrip from "../../components/MetricsStrip";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    overflowX: "auto",
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  rowName: {
    fontWeight: 600,
  },
  messagePreview: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: 1.45,
  },
  actionsCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  mobileSection: {
    padding: theme.spacing(1.5),
  },
  mobileCards: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: theme.spacing(1.5),
  },
  mobileCard: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5),
    boxShadow: "none",
  },
  mobileCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    minWidth: 0,
  },
  mobileCardBody: {
    display: "grid",
    gap: theme.spacing(1),
  },
  mobileMetaRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  mobileMetaLabel: {
    fontSize: "0.78rem",
    opacity: 0.65,
  },
  loadMoreBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const getScopeMeta = (message) => {
  if (message?.geral) {
    return {
      label: "Compartilhada",
      backgroundColor: "#dcfce7",
      color: "#166534",
    };
  }

  return {
    label: "Pessoal",
    backgroundColor: "#e0f2fe",
    color: "#075985",
  };
};

const QuickMessages = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickMessage, setSelectedQuickMessage] = useState(null);
  const [deletingQuickMessage, setDeletingQuickMessage] = useState(null);
  const [quickMessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickMessages, setQuickMessages] = useState([]);
  const [reloadFlag, setReloadFlag] = useState(0);

  const summary = useMemo(() => {
    const withAttachment = quickMessages.filter((message) => Boolean(message.mediaName)).length;
    const shared = quickMessages.filter((message) => message.geral).length;
    const visible = quickMessages.filter((message) => message.visao).length;

    return {
      loaded: quickMessages.length,
      withAttachment,
      shared,
      visible,
    };
  }, [quickMessages]);

  const summaryCards = useMemo(
    () => [
      { key: "loaded", label: "Carregadas", value: summary.loaded },
      { key: "visible", label: "Visiveis", value: summary.visible },
      { key: "attachment", label: "Com anexo", value: summary.withAttachment },
      { key: "shared", label: "Compartilhadas", value: summary.shared },
    ],
    [summary]
  );

  const fetchQuickMessages = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });

      setQuickMessages((currentMessages) => {
        const incomingMessages = Array.isArray(data.records) ? data.records : [];

        if (pageNumber === 1) {
          return incomingMessages;
        }

        const mergedMap = new Map(currentMessages.map((message) => [message.id, message]));
        incomingMessages.forEach((message) => {
          mergedMap.set(message.id, message);
        });
        return Array.from(mergedMap.values());
      });
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, searchParam]);

  useEffect(() => {
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchQuickMessages();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchQuickMessages, reloadFlag]);

  useEffect(() => {
    const companyId = user.companyId;

    const onQuickMessageEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        setQuickMessages((currentMessages) => {
          const messageIndex = currentMessages.findIndex(
            (message) => message.id === data.record.id
          );

          if (messageIndex !== -1) {
            const updatedMessages = [...currentMessages];
            updatedMessages[messageIndex] = data.record;
            return updatedMessages;
          }

          return [data.record, ...currentMessages];
        });
      }

      if (data.action === "delete") {
        setQuickMessages((currentMessages) =>
          currentMessages.filter((message) => message.id !== +data.id)
        );
      }
    };

    socket.on(`company-${companyId}-quickemessage`, onQuickMessageEvent);

    return () => {
      socket.off(`company-${companyId}-quickemessage`, onQuickMessageEvent);
    };
  }, [socket, user.companyId]);

  const refreshList = () => {
    setPageNumber(1);
    setReloadFlag((current) => current + 1);
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickMessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickMessage(null);
    setQuickMessageDialogOpen(false);
    refreshList();
  };

  const handleEditQuickMessage = (quickMessage) => {
    setSelectedQuickMessage(quickMessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickMessage = async (quickMessageId) => {
    try {
      await api.delete(`/quick-messages/${quickMessageId}`);
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      setDeletingQuickMessage(null);
      setConfirmModalOpen(false);
      refreshList();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const renderActionsMenu = (quickMessage) => (
    <RowActionsMenu
      buttonTitle={`Acoes da resposta rapida ${quickMessage.shortcode}`}
      actions={[
        {
          key: "edit",
          label: "Editar resposta rapida",
          icon: EditIcon,
          onClick: () => handleEditQuickMessage(quickMessage),
        },
        {
          key: "delete",
          label: "Excluir resposta rapida",
          icon: DeleteOutlineIcon,
          onClick: () => {
            setDeletingQuickMessage(quickMessage);
            setConfirmModalOpen(true);
          },
        },
      ]}
    />
  );

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={
          deletingQuickMessage &&
          `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickMessage.shortcode}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickMessage(deletingQuickMessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QuickMessageDialog
        resetPagination={refreshList}
        reload={refreshList}
        open={quickMessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickMessage && selectedQuickMessage.id}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {i18n.t("quickMessages.title")}
            </Typography>
          </Box>
          <Box className={classes.heroActions}>
            <Button variant="outlined" onClick={refreshList} disabled={loading}>
              Atualizar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenQuickMessageDialog}
            >
              {i18n.t("quickMessages.buttons.add")}
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores de respostas rapidas" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Respostas rapidas
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder={i18n.t("quickMessages.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar respostas rapidas" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${summary.loaded} carregadas`} />
              <Chip size="small" label={`Pagina ${pageNumber}`} />
            </Box>
          </Box>

          {loading && quickMessages.length === 0 ? (
            <Box p={2}>
              <TableRowSkeleton columns={5} />
            </Box>
          ) : isMobile ? (
            <Box className={classes.mobileSection}>
              <div className={classes.mobileCards}>
                {quickMessages.map((quickMessage) => {
                  const scopeMeta = getScopeMeta(quickMessage);

                  return (
                    <Paper key={quickMessage.id} className={classes.mobileCard}>
                      <div className={classes.mobileCardHeader}>
                        <div>
                          <Typography variant="body1" className={classes.rowName}>
                            /{quickMessage.shortcode}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            className={classes.messagePreview}
                          >
                            {quickMessage.message || "-"}
                          </Typography>
                        </div>

                        {renderActionsMenu(quickMessage)}
                      </div>

                      <div className={classes.mobileCardBody}>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>Escopo</Typography>
                          <Chip
                            size="small"
                            label={scopeMeta.label}
                            style={{
                              backgroundColor: scopeMeta.backgroundColor,
                              color: scopeMeta.color,
                            }}
                          />
                        </div>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>Anexo</Typography>
                          <Typography variant="body2">
                            {quickMessage.mediaName || i18n.t("quickMessages.noAttachment")}
                          </Typography>
                        </div>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>Visivel</Typography>
                          {quickMessage.visao ? (
                            <CheckCircleIcon style={{ color: "#166534" }} />
                          ) : (
                            <Typography variant="body2">Nao</Typography>
                          )}
                        </div>
                      </div>
                    </Paper>
                  );
                })}
              </div>
            </Box>
          ) : (
            <TableContainer className={classes.tableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>
                      {i18n.t("quickMessages.table.shortcode")}
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      {i18n.t("quickMessages.table.message")}
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      {i18n.t("quickMessages.table.mediaName")}
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>Escopo</TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("quickMessages.table.status")}
                    </TableCell>
                    <TableCell align="right" className={classes.actionsCell}>
                      {i18n.t("quickMessages.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quickMessages.map((quickMessage) => {
                    const scopeMeta = getScopeMeta(quickMessage);

                    return (
                      <TableRow key={quickMessage.id} hover>
                        <TableCell>
                          <Typography variant="body2" className={classes.rowName}>
                            /{quickMessage.shortcode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" className={classes.messagePreview}>
                            {quickMessage.message || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {quickMessage.mediaName || i18n.t("quickMessages.noAttachment")}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={scopeMeta.label}
                            style={{
                              backgroundColor: scopeMeta.backgroundColor,
                              color: scopeMeta.color,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {quickMessage.visao ? (
                            <CheckCircleIcon style={{ color: "#166534" }} />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="right" className={classes.actionsCell}>
                          {renderActionsMenu(quickMessage)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && quickMessages.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6">Nenhuma resposta rapida encontrada</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou cadastre uma nova mensagem para continuar.
              </Typography>
            </Box>
          )}

          {loading && quickMessages.length > 0 && (
            <Box p={2}>
              <TableRowSkeleton columns={5} />
            </Box>
          )}

          {!loading && hasMore && quickMessages.length > 0 && (
            <div className={classes.loadMoreBox}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setPageNumber((current) => current + 1)}
              >
                Carregar mais mensagens
              </Button>
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default QuickMessages;
